import { Link, NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import ErrorBoundary from "@/components/ErrorBoundary";
import { useEffect, useState, useCallback } from "react";
import { Users, LogOut, ChevronDown, Clock, BarChart3, Inbox, Kanban, ShieldCheck, AlertTriangle, Shield, HardDrive, ExternalLink, MessageSquare, Mail, Sun, CalendarDays } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import TimerWidget from "@/components/TimerWidget";
import SidebarClocks from "@/components/SidebarClocks";
import ClientNavHoverPanel from "@/components/ClientNavHoverPanel";
import useInboxSSE from "@/hooks/useInboxSSE";
import { GCHAT_ENABLED } from "@/lib/featureFlags";
import { prefetch as prefetchCache } from "@/lib/useCached";
import { toast } from "sonner";
import { decodeHtmlEntities } from "@/lib/sanitizeHtml";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// Tiny utility — strip noisy whitespace + decode HTML entities (iter 110q)
// before showing the body snippet in a toast. Keeps the popover human-readable
// (otherwise users saw `It&#39;s` instead of `It's` from Gmail's snippet field).
const cleanSnippet = (s = "") =>
  decodeHtmlEntities(s).replace(/[\r\n]+/g, " ").replace(/\s+/g, " ").trim().slice(0, 100);

// Iter 93 — Per-route preload map. When the user hovers/focuses a nav
// item we kick off the URLs that route is known to need so by the time
// they click, data is already in the SWR cache.
const PRELOAD_FOR_ROUTE = {
  "/": ["/me/today", "/clients", "/users"],
  "/triage": ["/clients", "/users", "/gmail/config", "/gmail/labels"],
  "/inbox": ["/clients", "/users", "/gmail/config", "/gmail/labels"],
  "/board": ["/clients", "/users"],
  "/clients": [],
  "/templates": ["/templates"],
  "/messages": [],
  "/firm-drive": [],
};

const NavItem = ({ to, icon: Icon, label, end, badge }) => (
  <NavLink
    to={to}
    end={end}
    data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
    onMouseEnter={() => {
      const urls = PRELOAD_FOR_ROUTE[to];
      if (urls) urls.forEach(prefetchCache);
    }}
    onFocus={() => {
      const urls = PRELOAD_FOR_ROUTE[to];
      if (urls) urls.forEach(prefetchCache);
    }}
    className={({ isActive }) =>
      `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
        isActive
          ? "bg-slate-900 text-white"
          : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
      }`
    }
  >
    <Icon className="h-4 w-4" strokeWidth={1.75} />
    <span className="flex-1">{label}</span>
    {badge > 0 && (
      <span
        data-testid={`nav-badge-${label.toLowerCase().replace(/\s+/g, "-")}`}
        className="ml-auto inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-semibold leading-none tabular-nums"
      >
        {badge > 99 ? "99+" : badge}
      </span>
    )}
  </NavLink>
);

export default function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [firmDriveId, setFirmDriveId] = useState(null);
  const [unread, setUnread] = useState({ inbox: 0, chat: 0 });
  // Iter 88 — Cmd/Ctrl-K routes the user to /triage and fires a custom
  // event picked up by the inline `InboxSearchDropdown`. No global modal
  // overlay anymore — the search lives inside the inbox toolbar.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        if (!location.pathname.startsWith("/triage")) {
          navigate("/triage");
          // Wait for Triage to mount before firing the focus event.
          setTimeout(() => window.dispatchEvent(new CustomEvent("inbox:focus-search")), 250);
        } else {
          window.dispatchEvent(new CustomEvent("inbox:focus-search"));
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [location.pathname, navigate]);
  useEffect(() => {
    api.get("/drive/config")
      .then((r) => setFirmDriveId(r.data?.firm_shared_drive_id || null))
      .catch(() => {});
  }, []);

  // Iter 49 — Sidebar unread badges. Cheap (<5ms) — single endpoint
  // bundles inbox + chat counts. Polled every 60s as a safety net; the SSE
  // listener below is the primary trigger.
  const refreshCounts = useCallback(async () => {
    try {
      const r = await api.get("/me/unread-counts");
      setUnread({ inbox: r.data?.inbox ?? 0, chat: r.data?.chat ?? 0 });
    } catch (e) { /* swallow — non-critical */ }
  }, []);
  useEffect(() => {
    refreshCounts();
    const id = setInterval(refreshCounts, 60_000);
    return () => clearInterval(id);
  }, [refreshCounts]);

  // Mark chat as read when the user opens /chat — zeros the badge & the
  // backend counter. Keep refreshCounts dep stable so we don't re-fire on
  // every render.
  // Iter 110y — skip this entirely when GChat is disabled (route no longer
  // mounted, so the URL path can only be hit by a stale bookmark; the
  // endpoint would 200-noop anyway).
  useEffect(() => {
    if (!GCHAT_ENABLED) return;
    if (location.pathname.startsWith("/chat")) {
      api.post("/me/mark-chat-read").then(refreshCounts).catch(() => {});
    }
  }, [location.pathname, refreshCounts]);

  // Iter 49 — App-wide SSE listener. Toasts new emails bottom-right
  // (iter 110l — moved from bottom-left per user request), even when the
  // user is on /board or /clients. Also re-pulls the badge count so it's
  // instant (not 60s lagged).
  const apiBase = process.env.REACT_APP_BACKEND_URL || "";
  useInboxSSE(apiBase, (event) => {
    if (event.type === "email.new" && event.patch) {
      const p = event.patch;
      const isDraft = Array.isArray(p.gmail_label_ids) && p.gmail_label_ids.includes("DRAFT");
      if (isDraft) return;
      if (p.direction !== "inbound") return;
      const sender = p.from_name || p.from_email || "Unknown";
      const subj = p.subject || "(no subject)";
      const snip = cleanSnippet(p.snippet || p.body || "");
      // Sonner toast — bottom-right, persistent enough to read, click to open.
      toast(subj, {
        description: (
          <div className="text-[12px] leading-snug">
            <div className="font-medium text-slate-800">{sender}</div>
            <div className="text-slate-600 line-clamp-2 mt-0.5">{snip}</div>
          </div>
        ),
        icon: <Mail className="h-4 w-4 text-blue-600"/>,
        position: "bottom-right",
        duration: 6000,
        action: {
          label: "Open",
          onClick: () => navigate("/"),
        },
      });
      // SSE fired BEFORE the DB count refresh would; bump optimistically.
      setUnread((u) => ({ ...u, inbox: u.inbox + 1 }));
    } else if (event.type === "email.updated" || event.type === "email.deleted") {
      // Status flips (read/archive/delete) can change the inbox count —
      // refresh after a tiny delay so the backend has fully written.
      setTimeout(refreshCounts, 250);
    }
  });

  return (
    <div className="min-h-screen flex bg-[#FAFAFA]">
      {/* Sidebar */}
      <aside className="w-60 shrink-0 border-r border-slate-200 bg-white flex flex-col sticky top-0 h-screen z-30">
        <div className="px-5 py-5 border-b border-slate-200">
          <Link to="/" className="block" data-testid="sidebar-logo">
            <div className="font-display font-bold tracking-tight text-slate-900 leading-none text-lg uppercase">CLOSE</div>
            <div className="text-[9px] uppercase tracking-[0.14em] text-slate-400 mt-1">Powered by Prysm Financial</div>
          </Link>
        </div>

        {/* Time-recording widget + 'My timesheet' link + today's date */}
        <div className="px-3 pt-3 pb-2 border-b border-slate-200 space-y-1.5">
          <div className="px-1">
            <TimerWidget />
          </div>
          <NavLink
            to="/timesheet"
            data-testid="sidebar-my-timesheet-link"
            className={({ isActive }) =>
              `block px-2 text-[11px] transition-colors ${
                isActive ? "text-slate-900 font-semibold underline" : "text-slate-500 hover:text-slate-900 hover:underline"
              }`
            }
          >My timesheet</NavLink>
          <SidebarClocks />
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 px-2 text-left" data-testid="sidebar-date">
            {new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
          </div>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 px-3 mb-2">Workspace</div>
          <NavItem to="/" icon={Sun} label="Today" end />
          <NavItem to="/triage" icon={Inbox} label="Inbox" badge={unread.inbox} />
          {/* Iter 110y — Chat nav hidden while the module is disabled. */}
          {GCHAT_ENABLED && (
            <NavItem to="/chat" icon={MessageSquare} label="Chat" badge={unread.chat} />
          )}
          <ClientNavHoverPanel>
            <NavItem to="/clients" icon={Users} label="Clients" />
          </ClientNavHoverPanel>
          <NavItem to="/board" icon={Kanban} label="Board" />
          <NavItem to="/filings" icon={AlertTriangle} label="Statutory filings" />
          {firmDriveId && (
            <NavItem to="/firm-drive" icon={HardDrive} label="Firm Drive" data-testid="nav-firm-drive"/>
          )}
          {/* Iter 142 — Admin section visibility is driven by effective
              permissions, not hard role checks. A reviewer granted
              `view_rota_variance` via the Permission Matrix now sees
              the Rota link without being bumped to manager. */}
          {(() => {
            const perms = new Set(user?.effective_permissions || []);
            const seesTeam = user?.role === "admin";
            const seesFirmSettings = user?.role === "admin";
            const seesTimeReports = perms.has("view_time_reports");
            const seesRota = perms.has("view_rota_variance");
            const showAdminHeader = seesTeam || seesFirmSettings || seesTimeReports || seesRota;
            if (!showAdminHeader) return null;
            return (
              <>
                <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 px-3 mt-6 mb-2">Admin</div>
                {seesTeam && <NavItem to="/team" icon={Users} label="Team" />}
                {seesFirmSettings && <NavItem to="/admin" icon={ShieldCheck} label="Firm Settings" />}
                {seesTimeReports && <NavItem to="/time-reports" icon={BarChart3} label="Time reports" />}
                {seesRota && <NavItem to="/rota" icon={CalendarDays} label="Rota & Variance" data-testid="nav-rota" />}
              </>
            );
          })()}
        </nav>

        <div className="p-3 border-t border-slate-200">
          <DropdownMenu>
            <DropdownMenuTrigger
              data-testid="user-menu-trigger"
              className="w-full flex items-center gap-3 px-2 py-2 rounded-md hover:bg-slate-100 transition-colors text-left"
            >
              <div className="h-8 w-8 rounded-sm bg-slate-200 overflow-hidden shrink-0">
                {user?.picture ? <img src={user.picture} alt="" className="w-full h-full object-cover"/> : null}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-900 truncate">{user?.name}</div>
                <div className="text-xs text-slate-500 capitalize">{user?.role}</div>
              </div>
              <ChevronDown className="h-4 w-4 text-slate-400" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="profile-menu-item" asChild>
                <Link to="/profile"><ShieldCheck className="h-4 w-4 mr-2"/> Profile & 2FA</Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="logout-button" onClick={logout} className="text-red-600 focus:text-red-700">
                <LogOut className="h-4 w-4 mr-2"/> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-x-hidden flex flex-col">
        <div className="flex-1 overflow-y-auto">
          {/* Iter 115 — Route-level ErrorBoundary. resetKey flips on
              route change so a stuck fallback doesn't outlive its page. */}
          <ErrorBoundary scope="route" resetKey={location.pathname}>
            <Outlet />
          </ErrorBoundary>
        </div>
      </main>
    </div>
  );
}
